import { usePanel } from "@/context/panel-context";
import { Drawer } from "@/components/ui/drawer";
import { Suspense, useState, useCallback } from "react";
import { Loader2 } from "lucide-react";
import UnifiedModelLayout from "./unified-model-layout";
import MetadataDrawer from "./metadata-drawer";
import RightPanelLayout from "./right-panel-layout";

const LoadingSpinner = () => (
  <div className="flex justify-center items-center h-full w-full">
    <Loader2 className="text-white w-8 h-8 animate-spin" />
  </div>
);

// Panel types that render as an overlay drawer (as opposed to a resizable
// third column). Keeping this in one place so protected-route.tsx and this
// drawer stay in sync.
const DRAWER_PANEL_TYPES = [
  "UNIFIED_MODEL",
  "METADATA_VIEWER",
  "MARKDOWN_RENDER",
  "MEDIA_PREVIEW",
];

export default function PanelDrawer() {
  const { panelType, setPanelType } = usePanel();
  const [headerActions, setHeaderActions] = useState<React.ReactNode>(null);

  const handleClose = () => {
    setPanelType(null);
  };

  const handleHeaderActionsReady = useCallback((actions: React.ReactNode) => {
    setHeaderActions(actions);
  }, []);

  if (!panelType || !DRAWER_PANEL_TYPES.includes(panelType)) {
    return null;
  }

  if (panelType === "UNIFIED_MODEL") {
    return (
      <Drawer
        isOpen={true}
        onClose={handleClose}
        title="Semantic Model"
        size="large"
        headerActions={headerActions}
      >
        <Suspense fallback={<LoadingSpinner />}>
          <UnifiedModelLayout onHeaderActionsReady={handleHeaderActionsReady} />
        </Suspense>
      </Drawer>
    );
  }

  if (panelType === "METADATA_VIEWER") {
    return (
      <Drawer
        isOpen={true}
        onClose={handleClose}
        title="Component Metadata"
        size="large"
      >
        <Suspense fallback={<LoadingSpinner />}>
          <MetadataDrawer />
        </Suspense>
      </Drawer>
    );
  }

  // Document/media preview (BREEZEAI-1044 UX polish): the viewer used to mount
  // as a third resizable column, which squeezed the middle "Functional
  // Requirements" panel down to unreadable widths. Render it as an overlay
  // drawer instead so the underlying ontology-manager layout keeps its size.
  // Width matches the roughly-half-screen footprint the old resizable column
  // settled at; `twMerge` in `cn` lets the className override the size preset.
  // No drawer title — RightPanelLayout has its own toolbar (Open / Download /
  // Copy / X) that acts as the header.
  if (panelType === "MARKDOWN_RENDER" || panelType === "MEDIA_PREVIEW") {
    return (
      <Drawer
        isOpen={true}
        onClose={handleClose}
        className="w-1/2 max-w-none"
      >
        <Suspense fallback={<LoadingSpinner />}>
          <RightPanelLayout />
        </Suspense>
      </Drawer>
    );
  }

  return null;
}
